const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://codelens.epoch.co/openshield/';

class MitreApiService {
  /**
   * Get MITRE ATT&CK data for a specific agent
   * @param {string} agentId - The agent ID
   * @param {number} days - Number of days to look back (default: 30)
   * @returns {Promise<Object>} MITRE ATT&CK data
   */
  static async getMitreAttackData(agentId, days = 30) {
    try {
      const accessToken = localStorage.getItem('cognito_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${API_BASE_URL}mitre/?agent_id=${agentId}&days=${days}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('cognito_token');
          localStorage.removeItem('cognito_id_token');
          window.location.href = 'https://codelens.epoch.co/';
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching MITRE ATT&CK data:', error);
      throw error;
    }
  }

  /**
   * Get MITRE ATT&CK events filtered by tactic
   * @param {string} agentId - The agent ID
   * @param {string} tactic - The tactic to filter by
   * @param {number} days - Number of days to look back (default: 30)
   * @returns {Promise<Array>} Filtered MITRE ATT&CK events
   */
  static async getMitreEventsByTactic(agentId, tactic, days = 30) {
    try {
      const data = await this.getMitreAttackData(agentId, days);
      
      if (!data.events || !Array.isArray(data.events)) {
        return [];
      }

      // Filter events by the specified tactic
      return data.events.filter(event => 
        event.mitre && 
        event.mitre.tactic && 
        event.mitre.tactic.includes(tactic)
      );
    } catch (error) {
      console.error('Error fetching MITRE events by tactic:', error);
      throw error;
    }
  }

  /**
   * Get unique techniques for a specific tactic
   * @param {string} agentId - The agent ID
   * @param {string} tactic - The tactic to get techniques for
   * @param {number} days - Number of days to look back (default: 30)
   * @returns {Promise<Array>} Array of unique techniques
   */
  static async getTechniquesByTactic(agentId, tactic, days = 30) {
    try {
      const events = await this.getMitreEventsByTactic(agentId, tactic, days);
      
      const techniques = new Map();
      
      events.forEach(event => {
        if (event.mitre && event.mitre.technique && event.mitre.id) {
          const techniqueArray = Array.isArray(event.mitre.technique) 
            ? event.mitre.technique 
            : [event.mitre.technique];
          const idArray = Array.isArray(event.mitre.id) 
            ? event.mitre.id 
            : [event.mitre.id];
          
          techniqueArray.forEach((technique, index) => {
            const id = idArray[index] || idArray[0];
            const key = `${id}-${technique}`;
            
            if (techniques.has(key)) {
              techniques.get(key).count++;
            } else {
              techniques.set(key, {
                id,
                technique,
                count: 1,
                tactic
              });
            }
          });
        }
      });
      
      return Array.from(techniques.values()).sort((a, b) => b.count - a.count);
    } catch (error) {
      console.error('Error fetching techniques by tactic:', error);
      throw error;
    }
  }

  /**
   * Get detailed information for a specific tactic
   * @param {string} agentId - The agent ID
   * @param {string} tactic - The tactic to get details for
   * @param {number} days - Number of days to look back (default: 30)
   * @returns {Promise<Object>} Detailed tactic information
   */
  static async getTacticDetails(agentId, tactic, days = 30) {
    try {
      const data = await this.getMitreAttackData(agentId, days);
      
      if (!data.events || !Array.isArray(data.events)) {
        return {
          tactic,
          count: 0,
          events: [],
          techniques: []
        };
      }

      // Filter events for the specific tactic
      const tacticEvents = data.events.filter(event => 
        event.mitre && 
        event.mitre.tactic && 
        (Array.isArray(event.mitre.tactic) 
          ? event.mitre.tactic.includes(tactic)
          : event.mitre.tactic === tactic)
      );

      // Get techniques for this tactic
      const techniques = await this.getTechniquesByTactic(agentId, tactic, days);
      
      // Sort events by timestamp (most recent first)
      const sortedEvents = tacticEvents.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      
      return {
        tactic,
        count: tacticEvents.length,
        events: sortedEvents,
        techniques
      };
    } catch (error) {
      console.error('Error fetching tactic details:', error);
      throw error;
    }
  }

  /**
   * Get summary statistics for MITRE ATT&CK data
   * @param {Object} data - The MITRE ATT&CK data
   * @returns {Object} Summary statistics
   */
  static getMitreSummary(data) {
    if (!data || !data.events || !Array.isArray(data.events)) {
      return {
        totalEvents: 0,
        uniqueTechniques: 0,
        uniqueTactics: 0,
        topTactic: null,
        criticalEvents: 0
      };
    }

    const events = data.events;
    const techniques = new Set();
    const tactics = new Set();
    let criticalEvents = 0;

    events.forEach(event => {
      if (event.mitre) {
        if (event.mitre.technique) {
          const techniqueArray = Array.isArray(event.mitre.technique) 
            ? event.mitre.technique 
            : [event.mitre.technique];
          techniqueArray.forEach(tech => techniques.add(tech));
        }
        
        if (event.mitre.tactic) {
          const tacticArray = Array.isArray(event.mitre.tactic) 
            ? event.mitre.tactic 
            : [event.mitre.tactic];
          tacticArray.forEach(tac => tactics.add(tac));
        }
      }
      
      if (event.level >= 10) {
        criticalEvents++;
      }
    });

    const topTactic = data.top_tactics && data.top_tactics.length > 0 
      ? data.top_tactics[0] 
      : null;

    return {
      totalEvents: events.length,
      uniqueTechniques: techniques.size,
      uniqueTactics: tactics.size,
      topTactic,
      criticalEvents
    };
  }
}

// Export both the class and a default instance
export { MitreApiService };
export default MitreApiService;
